// Modal de políticas globales: clawback, tope del multiplicador, ventana del corte y pagos. Guardar
// sella un plan nuevo con las mismas reglas. Mockup: portal-admin-vl-comisiones.html
import { useState } from 'react';
import { toast } from 'sonner';
import type { RetailCommission } from '@fiado/type-kit';
import { CommissionBillingPeriodEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionBillingPeriodEnum';
import { CommissionWindowEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionWindowEnum';
import { userMessageOf } from '@/api/errors';
import { Button, Input, Modal, Switch } from '@/kit';
import { useUpdatePolicies } from '../../api/commissionQueries';
import {
    formToPolicies,
    policiesToForm,
    validatePolicies,
    type PoliciesFormErrors,
    type PoliciesFormValues,
} from '../../lib/policiesForm';
import { BILLING_PERIOD_LABEL, WINDOW_LABEL } from '../../lib/ruleText';
import { FIELD, LABEL, SECTION_TITLE } from '../fieldStyles';

interface PoliciesModalProps {
    policies: RetailCommission.CommissionPolicies | undefined;
    onClose: () => void;
}

export function PoliciesModal({ policies, onClose }: PoliciesModalProps) {
    const [values, setValues] = useState<PoliciesFormValues>(() => policiesToForm(policies));
    const [errors, setErrors] = useState<PoliciesFormErrors>({});
    const save = useUpdatePolicies();
    const set = (patch: Partial<PoliciesFormValues>) => {
        setValues((current) => ({ ...current, ...patch }));
    };

    const submit = () => {
        const found = validatePolicies(values);
        setErrors(found);
        if (Object.keys(found).length > 0) return;
        save.mutate(formToPolicies(values), {
            onSuccess: () => {
                toast.success('Políticas guardadas', {
                    description: 'Aplican desde ahora a todas las reglas.',
                });
                onClose();
            },
            onError: (error) => {
                toast.error(userMessageOf(error));
            },
        });
    };

    return (
        <Modal isOpen onClose={onClose} title="Políticas globales de comisiones" size="md">
            <form
                noValidate
                className="flex flex-col gap-4"
                onSubmit={(event) => {
                    event.preventDefault();
                    submit();
                }}
            >
                <section className="flex flex-col gap-2">
                    <h3 className={SECTION_TITLE}>Clawback</h3>
                    <p className="text-[11px] text-muted-foreground">
                        Aplica a <b>todas las reglas</b>. La sensibilidad a NPI M30 la deriva el
                        motor según la palanca de cada regla.
                    </p>
                    <Input
                        id="pol-cancelacion"
                        type="number"
                        min={0}
                        step={1}
                        label="Ventana cancelación cliente (días)"
                        value={values.cancelacionDias}
                        onChange={(event) => {
                            set({ cancelacionDias: event.target.value });
                        }}
                        error={errors.cancelacionDias}
                    />
                    <Input
                        id="pol-devolucion"
                        type="number"
                        min={0}
                        step={1}
                        label="Ventana devolución dispositivo (días)"
                        value={values.devolucionDias}
                        onChange={(event) => {
                            set({ devolucionDias: event.target.value });
                        }}
                        error={errors.devolucionDias}
                    />
                    <Input
                        id="pol-npi"
                        type="number"
                        min={0}
                        step={1}
                        label="Ventana NPI M30 (días)"
                        value={values.npiM30Dias}
                        onChange={(event) => {
                            set({ npiM30Dias: event.target.value });
                        }}
                        error={errors.npiM30Dias}
                    />
                </section>

                <section className="flex flex-col gap-2">
                    <h3 className={SECTION_TITLE}>Multiplicador</h3>
                    <Input
                        id="pol-cap"
                        type="number"
                        min={1}
                        step={0.1}
                        label="Tope del multiplicador (×)"
                        hint="Los multiplicadores del corte se multiplican entre sí: ×1.5 y ×2 dan ×3, y con tope ×2.5 se paga ×2.5."
                        value={values.cap}
                        onChange={(event) => {
                            set({ cap: event.target.value });
                        }}
                        error={errors.cap}
                    />
                </section>

                <section className="flex flex-col gap-1">
                    <h3 className={SECTION_TITLE}>Ventana del corte</h3>
                    <label htmlFor="pol-ventana" className={LABEL}>
                        Ventana
                    </label>
                    <select
                        id="pol-ventana"
                        className={FIELD}
                        value={values.ventana}
                        onChange={(event) => {
                            set({ ventana: event.target.value as CommissionWindowEnum });
                        }}
                    >
                        {Object.values(CommissionWindowEnum).map((value) => (
                            <option key={value} value={value}>
                                {WINDOW_LABEL[value]}
                            </option>
                        ))}
                    </select>
                    <p className="text-[10.5px] text-muted-foreground">
                        Hoy el motor corta por semana; la ventana elegida se aplica cuando el corte
                        la lea.
                    </p>
                </section>

                <section className="flex flex-col gap-2">
                    <h3 className={SECTION_TITLE}>Pagos</h3>
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <p className="text-[12px] font-semibold">
                                Depositar comisiones a la wallet
                            </p>
                            <p className="text-[10.5px] text-muted-foreground">
                                Apagado, todo se sigue calculando y queda pendiente con el motivo
                                «pagos pausados». Una cadena que paga por nómina lo apaga y sigue
                                usando el CSV del corte.
                            </p>
                        </div>
                        <Switch
                            checked={values.walletDeposits}
                            onCheckedChange={(walletDeposits) => {
                                set({ walletDeposits });
                            }}
                            aria-label="Depositar comisiones a la wallet"
                        />
                    </div>
                    <div>
                        <label htmlFor="pol-cobro" className={LABEL}>
                            Cobro a la cadena
                        </label>
                        <select
                            id="pol-cobro"
                            className={FIELD}
                            value={values.chainBillingPeriod}
                            onChange={(event) => {
                                set({
                                    chainBillingPeriod: event.target
                                        .value as CommissionBillingPeriodEnum,
                                });
                            }}
                        >
                            {Object.values(CommissionBillingPeriodEnum).map((value) => (
                                <option key={value} value={value}>
                                    {BILLING_PERIOD_LABEL[value]}
                                </option>
                            ))}
                        </select>
                        <p className="mt-1 text-[10.5px] text-muted-foreground">
                            Cada cuánto se genera el estado de cuenta de lo depositado para
                            cobrárselo a la cadena.
                        </p>
                    </div>
                </section>

                <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={onClose}>
                        Cancelar
                    </Button>
                    <Button type="submit" isLoading={save.isPending}>
                        Guardar
                    </Button>
                </div>
            </form>
        </Modal>
    );
}
